import CloseIcon from "@assets/icons/close.svg";

import useSystemStore from "@store/useSystemStore";
import { openThemePage } from "@utils/navigation.util";
import { IBaseTheme } from "@interfaces/sourcing.interface";
import { cn } from "@utils/tailwind.util";
import { ThemeBadge } from "./ThemeBadge";
import { Tooltip } from "@ui/Tooltip";

interface IThemeComponentProps {
  currentTheme?: IBaseTheme;
  isLevelOneBased?: boolean;
  onRemove?: (currentTheme: IBaseTheme, parentTheme: IBaseTheme | null) => void;
}

export const ThemeComponent = ({
  currentTheme,
  isLevelOneBased,
  onRemove,
}: IThemeComponentProps) => {
  const { isDarkMode } = useSystemStore();

  const currentThemeParentList: IBaseTheme[] = currentTheme?.parent_list ?? [];
  const visibleThemes = isLevelOneBased
    ? currentThemeParentList.filter((t) => t.level > 0)
    : currentThemeParentList;
  const showTooltip = visibleThemes.length > 1;

  const gradientBackGround = isDarkMode
    ? "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #313643) 50.41%)"
    : "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #F3F4F6) 50.41%)";

  const tooltipContent = (
    <div className="flex w-full flex-col overflow-hidden">
      {/* hide the last theme from tooltip content because it's the current theme */}
      {visibleThemes.slice(0, -1).map((theme, index) => {
        const parentTheme = currentThemeParentList[index - 1] ?? null;

        return (
          <div
            key={theme.id}
            onClick={() => openThemePage(currentTheme.id)}
            className={
              "group relative flex max-w-[310px] cursor-pointer flex-row items-center overflow-hidden transition-all"
            }
          >
            <ThemeBadge
              level={theme.level}
              title={theme.title}
              containerClassName="bg-transparent group-hover:bg-gray-50 dark:bg-transparent dark:group-hover:bg-stroke-primaryDark"
              size="medium"
              isLevelOneBased={isLevelOneBased}
            />

            <button
              className="absolute bottom-0 right-1 top-0 z-20 flex h-full w-4 items-center justify-end rounded-lg leading-[0] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onRemove?.(theme, parentTheme);
              }}
            >
              <div className="inline-block align-middle">
                <CloseIcon width={16} height={16} className="cursor-pointer" />
              </div>
            </button>
            <div
              className="pointer-events-none absolute right-0 top-0 z-10 h-full w-10 rounded-lg opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
              style={{ background: gradientBackGround }}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-w-0 zoom-in-80 relative duration-300 ease-in">
      {currentTheme && currentTheme.title.length > 0 ? (
        <Tooltip
          offset={({ side }) => (side.includes("top") ? 6 : -2)}
          variant="default"
          placement="top-start"
          disabled={!showTooltip}
          tooltipContent={tooltipContent}
          tooltipContentClassName={cn("p-0", { "p-1": showTooltip })}
          delay={{ open: 500, close: 100 }}
          maxWidth={280}
          minWidthFromTrigger
          className="w-full"
        >
          <div
            onClick={() => openThemePage(currentTheme.id)}
            className={
              "group relative flex max-w-[310px] cursor-pointer flex-row items-center overflow-hidden transition-all"
            }
          >
            <ThemeBadge
              level={currentTheme.level}
              title={currentTheme.title}
              size="medium"
              containerClassName="shadow-mainButton"
              isLevelOneBased={isLevelOneBased}
            />

            <button
              className="absolute bottom-0 right-1 top-0 z-20 flex h-full w-4 items-center justify-end rounded-lg leading-[0] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onRemove?.(currentTheme, null);
              }}
            >
              <div className="inline-block align-middle">
                <CloseIcon width={16} height={16} className="cursor-pointer" />
              </div>
            </button>
            <div
              className="pointer-events-none absolute right-0 top-0 z-10 h-full w-10 rounded-lg opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
              style={{ background: gradientBackGround }}
            />
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
};
